import { CSVImporter } from "csv-import-react";
import React, { useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { LIST_GROUPS, LIST_CLASSROOMS, REGISTER_USER, CREATE_USER_GROUP } from "../../graphql/admin-requests";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Chip,
  LinearProgress,
  Paper,
  Alert
} from '@mui/material';
import { CheckCircle as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';

const organization_uid = "1b2623f0-6b48-4619-a07a-7ee8720c86fe";
const groups = [
  "group1",
  "group2",
  "group3",
  "group4",
  "group5",
  "group6",
  "group7",
  "group8",
  "group9",
];

function formatPassword(user) {
  return user.values?.password || user.values?.first_name;
}

function getGroupes(user) {
  return groups
    .filter((group) => user.values[group] && user.values[group] !== "") // Filtre les groupes existants
    .map((group) => user.values[group]) // Extrait les valeurs des groupes existants
    .filter(Boolean); // Remove any undefined/null values
}

async function createUserInDatabase(user, registerUser, createUserGroup, allGroups) {
  try {
    // Create user and account
    const userResult = await registerUser({
      variables: {
        firstName: user.values.first_name,
        lastName: user.values.last_name,
        email: user.values.email,
        password: formatPassword(user),
        classroomUid: user.values.classroom_uid,
      },
    });

    const createdUserUid = userResult.data.registerUser.user.uid;
    console.log(`✅ Utilisateur créé: ${user.values.first_name} ${user.values.last_name}`);

    // Add user to groups
    const userGroups = getGroupes(user);
    const groupMap = allGroups.reduce((map, group) => {
      map[group.name.toLowerCase()] = group.uid;
      return map;
    }, {});

    for (const groupName of userGroups) {
      if (groupName) {
        const groupUid = groupMap[groupName.toLowerCase()];
        if (groupUid) {
          await createUserGroup({
            variables: {
              userUid: createdUserUid,
              groupUid: groupUid,
            },
          });
          console.log(`✅ Utilisateur ${user.values.first_name} ajouté au groupe: ${groupName}`);
        }
      }
    }

    return { success: true, user: userResult.data.registerUser.user };
  } catch (error) {
    console.error(`❌ Erreur lors de la création de ${user.values.first_name} ${user.values.last_name}:`, error);
    return { success: false, error, user: user.values };
  }
}

function enrichUser(user, allClassrooms) {
  const res = user;
  
  // Find classroom UID from database based on name
  const classroom = allClassrooms.find(c => 
    c.name.toLowerCase() === user.values.classroom.toLowerCase()
  );
  
  if (!classroom) {
    console.error(`Classe non trouvée dans la base de données: ${user.values.classroom}`);
    throw new Error(`Classe "${user.values.classroom}" non trouvée dans la base de données`);
  }
  
  res.values.classroom_uid = classroom.uid;
  res.values.organization_uid = organization_uid;
  res.values.role = "horaroj_user";
  return res;
}

// Validation functions
function validateGroups(users, allGroups) {
  const errors = [];
  const groupNames = allGroups.map(group => group.name.toLowerCase());
  
  users.forEach((user, userIndex) => {
    const userGroups = getGroupes(user);
    userGroups.forEach(groupName => {
      if (groupName && !groupNames.includes(groupName.toLowerCase())) {
        errors.push({
          user: `${user.values.first_name} ${user.values.last_name}`,
          userIndex: userIndex + 1,
          field: groupName,
          type: 'group',
          message: `Groupe "${groupName}" non trouvé dans la base de données`
        });
      }
    });
  });
  
  return errors;
}

function validateClassrooms(users, allClassrooms) {
  const errors = [];
  const classroomNames = allClassrooms.map(classroom => classroom.name.toLowerCase());
  
  users.forEach((user, userIndex) => {
    const classroomName = user.values.classroom;
    if (classroomName && !classroomNames.includes(classroomName.toLowerCase())) {
      errors.push({
        user: `${user.values.first_name} ${user.values.last_name}`,
        userIndex: userIndex + 1,
        field: classroomName,
        type: 'classroom',
                  message: `Classe "${classroomName}" non trouvée dans la base de données`
      });
    }
  });
  
  return errors;
}

function validateEmails(users) {
  const errors = [];
  const csvEmails = new Map(); // Track emails within the CSV file
  
  users.forEach((user, userIndex) => {
    const email = user.values.email?.toLowerCase().trim();
    if (!email) {
      errors.push({
        user: `${user.values.first_name} ${user.values.last_name}`,
        userIndex: userIndex + 1,
        field: 'email',
        type: 'email',
        message: `Email manquant ou invalide`
      });
      return;
    }
    
    // Check for duplicates within the CSV file
    if (csvEmails.has(email)) {
      const firstOccurrence = csvEmails.get(email);
      errors.push({
        user: `${user.values.first_name} ${user.values.last_name}`,
        userIndex: userIndex + 1,
        field: email,
        type: 'email',
        message: `Email dupliqué dans le fichier CSV (première occurrence ligne ${firstOccurrence})`
      });
    } else {
      csvEmails.set(email, userIndex + 1);
    }
  });
  
  return errors;
}

async function transformData(data, onComplete, allGroups = [], allClassrooms = [], registerUser, createUserGroup, setIsImporting, setValidationErrors, setShowResults, setImportProgress, setImportResults) {
  if (data.error) {
    // Set critical error and show results modal
    setImportResults({
      total: 0,
      successful: 0,
      failed: 0,
      results: [],
      criticalError: "Échec du traitement d'importation"
    });
    setShowResults(true);
    return;
  } else {
    // Validate groups, classrooms, and emails before processing
    const groupValidationErrors = validateGroups(data.rows, allGroups);
    const classroomValidationErrors = validateClassrooms(data.rows, allClassrooms);
    const emailValidationErrors = validateEmails(data.rows);
    const allValidationErrors = [...groupValidationErrors, ...classroomValidationErrors, ...emailValidationErrors];
    
    if (allValidationErrors.length > 0) {
      console.error("Erreurs de validation:", allValidationErrors);
      
      setValidationErrors(allValidationErrors);
      setShowResults(true);
      
      // Don't throw error, just return early to prevent processing
      return;
    }
    
    setIsImporting(true);
    
    try {
      const enrichedUsers = data.rows.map(user => enrichUser(user, allClassrooms));
      const results = [];
      
      // Import users one by one to avoid overwhelming the server
      setImportProgress({ current: 0, total: enrichedUsers.length });
      
      for (let i = 0; i < enrichedUsers.length; i++) {
        const user = enrichedUsers[i];
        setImportProgress({ current: i + 1, total: enrichedUsers.length });
        
        const result = await createUserInDatabase(user, registerUser, createUserGroup, allGroups);
        results.push(result);
        
        // Small delay between operations to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      
      setImportResults({
        total: enrichedUsers.length,
        successful,
        failed,
        results
      });
      setIsImporting(false);
      setShowResults(true);
      
      // Call the callback if provided
      if (onComplete) {
        onComplete({ ...data, importResults: results });
      }
    } catch (error) {
      setIsImporting(false);
      setImportResults({
        total: 0,
        successful: 0,
        failed: 0,
        results: [],
        criticalError: error.message
      });
      setShowResults(true);
      // Don't throw error, just return early
      return;
    }
  }
}

function CSVUserImporter({ isOpen, onClose, onComplete }) {
  const [allGroups, setAllGroups] = useState([]);
  const [allClassrooms, setAllClassrooms] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const [getGroups] = useLazyQuery(LIST_GROUPS, {
    onCompleted: (data) => {
      setAllGroups(data.allGroups.nodes);
    },
    onError: (error) => {
      console.error("Erreur lors de la récupération des groupes:", error);
    },
  });

  const [getClassrooms] = useLazyQuery(LIST_CLASSROOMS, {
    onCompleted: (data) => {
      setAllClassrooms(data.allClassrooms.nodes);
    },
    onError: (error) => {
      console.error("Erreur lors de la récupération des classes:", error);
    },
  });



  const [registerUser] = useMutation(REGISTER_USER, {
    onError: (error) => {
      console.error("Erreur lors de la création d'utilisateur:", error);
    },
  });

  const [createUserGroup] = useMutation(CREATE_USER_GROUP, {
    onError: (error) => {
      console.error("Erreur lors de l'ajout au groupe:", error);
    },
  });

  // Load groups and classrooms when component mounts or modal opens
  React.useEffect(() => {
    if (isOpen) {
      if (allGroups.length === 0) {
        getGroups({ variables: { first: 1000 } }); // Get all groups
      }
      if (allClassrooms.length === 0) {
        getClassrooms({ variables: { first: 1000 } }); // Get all classrooms
      }
    }
  }, [isOpen, getGroups, getClassrooms, allGroups.length, allClassrooms.length]);

  const handleClose = () => {
    setShowResults(false);
    setImportResults(null);
    setValidationErrors([]);
    setImportProgress({ current: 0, total: 0 });
    onClose();
  };

  const ResultsModal = () => (
    <Dialog open={showResults} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {validationErrors.length > 0 ? (
          <>❌ Erreurs de validation</>
        ) : importResults?.criticalError ? (
          <>❌ Erreur critique</>
        ) : (
          <>🎉 Résultats d'importation</>
        )}
      </DialogTitle>
      <DialogContent>
        {validationErrors.length > 0 && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationErrors.length} erreur{validationErrors.length > 1 ? 's' : ''} trouvée{validationErrors.length > 1 ? 's' : ''}
            </Alert>
            
            {/* Group errors by type */}
            {['classroom', 'group', 'email'].map(type => {
              const errors = validationErrors.filter(err => err.type === type);
              if (errors.length === 0) return null;
              
              const getTitle = (type) => {
                switch(type) {
                  case 'classroom': return 'Erreurs de classes';
                  case 'group': return 'Erreurs de groupes';
                  case 'email': return 'Erreurs d\'emails';
                  default: return 'Erreurs';
                }
              };
              
              return (
                <Paper key={type} elevation={1} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {getTitle(type)} ({errors.length})
                  </Typography>
                  <List dense>
                    {errors.map((error, index) => (
                      <ListItem key={index}>
                        <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                        <ListItemText 
                          primary={`Ligne ${error.userIndex} - ${error.user}`}
                          secondary={error.message}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              );
            })}
            
            <Typography variant="body2" color="text.secondary">
              Veuillez corriger ces problèmes dans votre fichier CSV et réessayer.
            </Typography>
          </Box>
        )}
        
        {importResults?.criticalError && (
          <Alert severity="error">
            <Typography variant="body1">
              {importResults.criticalError}
            </Typography>
          </Alert>
        )}
        
        {importResults && !importResults.criticalError && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Chip 
                icon={<CheckIcon />} 
                label={`${importResults.successful} réussies`} 
                color="success" 
                variant="outlined"
              />
              {importResults.failed > 0 && (
                <Chip 
                  icon={<ErrorIcon />} 
                  label={`${importResults.failed} échouées`} 
                  color="error" 
                  variant="outlined"
                />
              )}
            </Box>
            
            {importResults.failed > 0 && (
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="error">
                  Utilisateurs échoués:
                </Typography>
                <List dense>
                  {importResults.results.filter(r => !r.success).map((result, index) => (
                    <ListItem key={index}>
                      <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                      <ListItemText 
                        primary={`${result.user.first_name} ${result.user.last_name}`}
                        secondary={result.error.message}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
            
            {importResults.successful > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                🎉 {importResults.successful} utilisateur{importResults.successful > 1 ? 's ' : ' '} 
                créé{importResults.successful > 1 ? 's' : ''} avec succès!
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <CSVImporter
        modalIsOpen={isOpen && !showResults}
        language="fr"
        modalOnCloseTriggered={handleClose}
        darkMode={true}
        disabled={isImporting}
        onComplete={(data) => {
          try {
            transformData(data, onComplete, allGroups, allClassrooms, registerUser, createUserGroup, setIsImporting, setValidationErrors, setShowResults, setImportProgress, setImportResults);
          } catch (error) {
            console.error('Erreur inattendue lors de l\'importation:', error);
            setImportResults({
              total: 0,
              successful: 0,
              failed: 0,
              results: [],
              criticalError: `Erreur inattendue: ${error.message}`
            });
            setShowResults(true);
            setIsImporting(false);
          }
        }}
      template={{
        columns: [
          {
            name: "Prenom",
            key: "first_name",
            required: true,
            description: "Le prenom de l'élève",
            suggested_mappings: ["First", "Firstname", "Prenom", "Prénom"],
          },
          {
            name: "Nom",
            key: "last_name",
            required: true,
            description: "Le nom de l'élève",
            suggested_mappings: ["Last", "Lastname", "Nom"],
          },
          {
            name: "Email",
            key: "email",
            required: true,
            description: "L'email de l'élève",
            suggested_mappings: ["Email", "Mail", "Office365"],
          },
          {
            name: "Classe",
            key: "classroom",
            required: true,
            description: "La classe de l'élève",
            suggested_mappings: ["Classe", "Code Classe"],
          },
          {
            name: "Mot de passe",
            key: "password",
            required: true,
            description: "Le mot de passe de l'élève",
            suggested_mappings: ["Mot de passe", "Password", "Date de naissance"],
          },
          {
            name: "Groupe 1",
            key: "group1",
            required: false,
            suggested_mappings: ["Groupe 1", "Code Option1", "Code Option 1"],
          },
          {
            name: "Groupe 2",
            key: "group2",
            required: false,
            suggested_mappings: ["Groupe 2", "Code Option2", "Code Option 2"],
          },
          {
            name: "Groupe 3",
            key: "group3",
            required: false,
            suggested_mappings: ["Groupe 3", "Code Option3", "Code Option 3"],
          },
          {
            name: "Groupe 4",
            key: "group4",
            required: false,
            suggested_mappings: ["Groupe 4", "Code Option4", "Code Option 4"],
          },
          {
            name: "Groupe 5",
            key: "group5",
            required: false,
            suggested_mappings: ["Groupe 5", "Code Option5", "Code Option 5"],
          },
          {
            name: "Groupe 6",
            key: "group6",
            required: false,
            suggested_mappings: ["Groupe 6", "Code Option6", "Code Option 6"],
          },
          {
            name: "Groupe 7",
            key: "group7",
            required: false,
            suggested_mappings: ["Groupe 7", "Code Option7", "Code Option 7"],
          },
          {
            name: "Groupe 8",
            key: "group8",
            required: false,
            suggested_mappings: ["Groupe 8", "Code Option interne1"],
          },
          {
            name: "Groupe 9",
            key: "group9",
            required: false,
            suggested_mappings: ["Groupe 9", "Code Option interne2"],
          },
        ],
      }}
    />
    
    {/* Import Progress Dialog */}
    <Dialog open={isImporting} disableEscapeKeyDown>
      <DialogTitle>⏳ Importation en cours...</DialogTitle>
      <DialogContent sx={{ width: 400 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Importation {importProgress.current} / {importProgress.total}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0}
            sx={{ mt: 1 }}
          />
        </Box>
        <Typography variant="body2">
          Veuillez patienter pendant que nous créons vos utilisateurs...
        </Typography>
      </DialogContent>
    </Dialog>
    
    <ResultsModal />
  </>
  );
}

export default CSVUserImporter;
